// MySQL create-table-expression slot recognizer for the EditorContext
// projection. PRD: GRA-8 (cross-engine projection pivot), GRA8-ROLL-2.
// G11: pure text scan, NO web-tree-sitter — this is the legal projection-side
// body the future tree-sitter swap replaces behind the identical signature.
//
// GRA8-ROLL-2 adds the two residual CREATE-TABLE in-progress-column slots
// that the DDL constraint family does NOT cover (CHECK / PRIMARY KEY / UNIQUE
// / FOREIGN KEY / GENERATED leads are owned by `classifyDdlSlot` in
// editorContextSlot.ddl2.ts — formerly by the retired string-walk
// `classifyDdlPositionKind`; without these two the cursor fell to the
// `unknown` arm where the resolver wrongly emits tables):
//   (a) `PARTITION BY HASH(▮` / `RANGE(` / `LIST(` / `KEY(` — the partition
//       expression references the in-progress table's columns.
//   2. a bare `INDEX <name>? (▮` / `KEY <name>? (▮` secondary-index column
//       list inside the CREATE TABLE body (NOT a PRIMARY/UNIQUE/FOREIGN KEY —
//       those are constraint leads the DDL family already owns).
// Both resolve to `'ddl-create-table-expression'`, so the mysql resolver emits
// the columns declared so far in the CREATE TABLE preamble. Pinned by mysql
// script.41 §2 (PARTITION BY HASH) + script.44 §3 (bare INDEX).
//
// `resolveSlotKind` dispatches `isMysqlCreateTableExpressionSlot` only on the
// `engine === 'mysql'` arm, AFTER the (absent) data-type arm and BEFORE the
// shared `ddlConstraintSlot` call.
//
// GRA8-ROLL-3 adds two more residual mysql slots the DDL family does NOT
// cover:
//   - `isMysqlFulltextSpatialIndexColumnSlot` — `CREATE FULLTEXT|SPATIAL INDEX
//     … ON tbl (▮`; the family's index-column recognizer accepts plain/UNIQUE
//     INDEX heads only, so FULLTEXT/SPATIAL falls through. Maps
//     to `'ddl-index-column'` → the resolver emits the indexed table's columns.
//   - `isMysqlDropColumnTargetSlot` — `ALTER TABLE … DROP COLUMN ▮`; no paren /
//     REFERENCES / trigger so the family returns null. Maps to
//     `'ddl-alter-table-constraint-column-list'` → the resolver emits the
//     altered table's live columns. Pinned by mysql script.31 §7 + script.30 §8.
//
// GRA8-ROLL-4 adds the procedural-header data-type slot:
//   - `isMysqlDataTypeSlot` — `CREATE PROCEDURE|FUNCTION (… <param> [IN|OUT|
//     INOUT] ▮`; mysql had no data-type emission before this slice, so the type
//     slot fell to `unknown` and leaked IN/OUT keywords + table names. Maps to
//     `'data-type'` → the resolver emits the MySQL type vocabulary. MySQL params
//     carry no `@`-sigil, so the pilot's `isMssqlDataTypeSlot` does not cover
//     them. Dispatched first on the `engine === 'mysql'` arm (mirrors the mssql
//     data-type arm). Pinned by mysql script.33 §2.

import type { ClauseToken } from './editorContextClause';
import { headTableVerb } from './editorContextSlot.ddl';
import {
  dropAttachedPartial,
  findEnclosingLparenIndex,
  upper,
} from './editorContextSlotShared';

// Partition-method function names that may sit between `BY` and the `(` opener
// (`PARTITION BY HASH(`, `PARTITION BY LINEAR KEY(`, `… RANGE COLUMNS(`, …).
const PARTITION_METHODS: ReadonlySet<string> = new Set([
  'HASH', 'RANGE', 'LIST', 'KEY', 'LINEAR', 'COLUMNS',
]);

// Index-type prefixes that mark the opener as a constraint/unique key the
// DDL family already owns — the slot must NOT double-classify.
const OWNED_KEY_LEADS: ReadonlySet<string> = new Set([
  'PRIMARY', 'UNIQUE', 'FOREIGN',
]);

/**
 * True when the cursor sits inside an unclosed `(` whose enclosing statement is
 * a CREATE TABLE AND the opener is preceded by either a `PARTITION BY <method>`
 * expression or a bare `INDEX`/`KEY` secondary-index column list. Returns false
 * for the PRIMARY/UNIQUE/FOREIGN KEY constraint leads (owned by the regex DDL
 * classifier) so the two paths never collide.
 */
export function isMysqlCreateTableExpressionSlot(
  tokens: readonly ClauseToken[],
): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return false;
  if (headTableVerb(tokens) !== 'create') return false;

  if (precededByPartitionBy(tokens, lparenIdx)) return true;
  if (precededByBareIndexOrKey(tokens, lparenIdx)) return true;
  return false;
}

/**
 * True when the unclosed `(` is the partition-expression opener: walking back
 * from the opener over an optional partition-method function name (HASH / RANGE
 * / LIST / KEY / LINEAR / COLUMNS), the next keywords are `BY` then `PARTITION`.
 */
function precededByPartitionBy(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): boolean {
  let i = lparenIdx - 1;
  // Tolerate a chain of intervening method tokens (`LINEAR KEY`, `RANGE
  // COLUMNS`, plain `HASH`), each a keyword/identifier in PARTITION_METHODS.
  while (i >= 0) {
    const u = upper(tokens[i]);
    if (u !== null && PARTITION_METHODS.has(u)) {
      i -= 1;
      continue;
    }
    break;
  }
  if (upper(tokens[i]) !== 'BY') return false;
  return upper(tokens[i - 1]) === 'PARTITION';
}

/**
 * True when the unclosed `(` is a bare secondary-index column-list opener:
 * `INDEX (`, `INDEX ix_name (`, `KEY (`, `KEY ix_name (`. The opener is
 * preceded by an optional index-name identifier whose prior keyword is `INDEX`
 * or `KEY`. A `KEY` whose own prior keyword is PRIMARY / UNIQUE / FOREIGN is a
 * constraint lead the regex classifier owns and is excluded here.
 */
function precededByBareIndexOrKey(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): boolean {
  // `INDEX` lexes as a keyword and `KEY` as an identifier, so probe by
  // uppercased text. The token just before `(` is either the lead keyword
  // itself (`INDEX (` / `KEY (`) or an index-name identifier whose prior token
  // is the lead (`INDEX ix (` / `KEY ix (`).
  let i = lparenIdx - 1;
  if (isIndexLead(upper(tokens[i]))) {
    return notOwnedKeyLead(tokens, i);
  }
  if (i >= 0 && tokens[i]!.kind === 'identifier') {
    i -= 1;
    if (isIndexLead(upper(tokens[i]))) {
      return notOwnedKeyLead(tokens, i);
    }
  }
  return false;
}

function isIndexLead(u: string | null): boolean {
  return u === 'INDEX' || u === 'KEY';
}

// Index types the DDL family's plain/UNIQUE index-column recognizer does NOT
// cover — the residual this recognizer owns (GRA8-ROLL-3).
const FULLTEXT_SPATIAL_INDEX_TYPES: ReadonlySet<string> = new Set([
  'FULLTEXT', 'SPATIAL',
]);

/**
 * True when the cursor sits inside an unclosed `(` whose enclosing statement is
 * `CREATE [OR REPLACE] FULLTEXT|SPATIAL INDEX <name> ON <tbl> (▮`. The opener is
 * the indexed table's column list. GRA8-ROLL-3 — only the FULLTEXT/SPATIAL
 * residual: plain / UNIQUE `CREATE INDEX` is owned by `classifyDdlSlot`
 * (no double-classify), so this recognizer returns false there.
 */
export function isMysqlFulltextSpatialIndexColumnSlot(
  tokens: readonly ClauseToken[],
): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return false;
  // The opener must be preceded by `ON <tbl>` (bare or schema-qualified).
  if (!precededByOnTable(tokens, lparenIdx)) return false;
  // The head must declare a FULLTEXT/SPATIAL INDEX: walking forward from a
  // `CREATE`, the next type keyword (tolerating OR/REPLACE noise) is
  // FULLTEXT or SPATIAL, immediately followed by INDEX.
  return headIsFulltextSpatialIndex(tokens, lparenIdx);
}

/**
 * True when the unclosed `(` at `lparenIdx` is immediately preceded by an
 * `ON <table>` target (bare `ON tbl (` or schema-qualified `ON db.tbl (`).
 */
function precededByOnTable(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): boolean {
  let i = lparenIdx - 1;
  if (i < 0 || tokens[i]!.kind !== 'identifier') return false;
  i -= 1;
  if (i >= 0 && tokens[i]!.kind === 'dot') {
    i -= 1;
    if (i < 0 || tokens[i]!.kind !== 'identifier') return false;
    i -= 1;
  }
  return upper(tokens[i]) === 'ON';
}

/**
 * Scan the head tokens before `lparenIdx` for a `CREATE … FULLTEXT|SPATIAL …
 * INDEX` lead. Tolerates `OR REPLACE` noise between CREATE and the index type.
 * Returns the verb of the LAST such lead before the cursor (closest statement).
 */
function headIsFulltextSpatialIndex(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): boolean {
  const NOISE = new Set(['OR', 'REPLACE']);
  let found = false;
  for (let i = 0; i < lparenIdx; i += 1) {
    if (upper(tokens[i]) !== 'CREATE') continue;
    // Skip OR/REPLACE noise, then require a FULLTEXT/SPATIAL keyword whose
    // next non-noise keyword is INDEX.
    let j = i + 1;
    while (j < lparenIdx && NOISE.has(upper(tokens[j]) ?? '')) j += 1;
    const typeKw = upper(tokens[j]);
    if (typeKw === null || !FULLTEXT_SPATIAL_INDEX_TYPES.has(typeKw)) continue;
    if (upper(tokens[j + 1]) === 'INDEX') found = true;
  }
  return found;
}

/**
 * True when the cursor is the target of an `ALTER TABLE … DROP COLUMN ▮` slot:
 * NOT inside any unclosed `(`, and the tail is `DROP COLUMN` (with an optional
 * partial column identifier already typed) whose `COLUMN` is immediately
 * preceded by `DROP`, inside an `ALTER TABLE` statement. GRA8-ROLL-3 — the
 * DDL family returns null here (no paren / REFERENCES / trigger), so this
 * recognizer routes it to the altered table's live columns.
 */
export function isMysqlDropColumnTargetSlot(
  tokens: readonly ClauseToken[],
): boolean {
  if (findEnclosingLparenIndex(tokens) !== -1) return false;
  if (headTableVerb(tokens) !== 'alter') return false;

  let i = tokens.length - 1;
  // Optional trailing partial column identifier (`DROP COLUMN no`). `COLUMN`
  // itself lexes as an identifier, so only step over a trailing identifier that
  // is NOT the `COLUMN` keyword (otherwise the bare `DROP COLUMN ▮` slot would
  // consume `COLUMN` and miss).
  if (i >= 0 && tokens[i]!.kind === 'identifier' && upper(tokens[i]) !== 'COLUMN') {
    i -= 1;
  }
  if (upper(tokens[i]) !== 'COLUMN') return false;
  return upper(tokens[i - 1]) === 'DROP';
}

/**
 * Exclude PRIMARY/UNIQUE/FOREIGN KEY — those constraint leads belong to
 * the DDL family (`classifyDdlSlot`), not the residual secondary-index slot.
 * (`INDEX` is never a constraint lead, so it always passes.)
 */
function notOwnedKeyLead(tokens: readonly ClauseToken[], leadIdx: number): boolean {
  if (upper(tokens[leadIdx]) !== 'KEY') return true;
  const before = upper(tokens[leadIdx - 1]);
  return before === null || !OWNED_KEY_LEADS.has(before);
}

// MySQL stored-routine parameter mode keywords. `IN` lexes as a keyword (it is
// in SQL_KEYWORDS); `OUT` / `INOUT` lex as identifiers — `upper()` normalises
// both kinds, so a set lookup over the uppercased text covers all three plus the
// two-token `IN OUT` spelling MySQL also accepts. GRA8-ROLL-4.
const PARAM_MODE_KEYWORDS: ReadonlySet<string> = new Set(['IN', 'OUT', 'INOUT']);

/**
 * True when the cursor sits at a MySQL CREATE PROCEDURE/FUNCTION parameter
 * type slot: inside the routine's param-list `(` AND the prev tokens form a
 * fresh-param type slot `<paramName> [IN|OUT|INOUT|IN OUT] ▮`, where the param
 * name is an identifier preceded by the param-list `(` or a `,` separator.
 * MySQL params carry no `@`-sigil (unlike T-SQL), so the pilot's
 * `isMssqlDataTypeSlot` does not cover them. GRA8-ROLL-4.
 *
 * The CREATE PROCEDURE/FUNCTION head guard keeps `WHERE x IN (▮` (predicate
 * IN inside an unrelated paren) out of the data-type set: that paren's
 * enclosing statement head is not a CREATE routine, so the recognizer returns
 * false. Pinned by mysql script.33 §2 (the leak is `IN`/`OUT` keywords +
 * table names; correct data-type classification is what suppresses them).
 */
export function isMysqlDataTypeSlot(
  raw: readonly ClauseToken[],
  /** Cursor offset — distinguishes an ATTACHED partial from a ws-separated caret. */
  offset: number,
): boolean {
  // TYPED-PREFIX PARITY: drop a bare word the caret is ATTACHED to. `(… p_id
  // VAR▮` is the same type slot as `(… p_id ▮`; conversely `( I▮` (the user
  // typing the MODE keyword `IN`) must NOT read as `<name> ▮` — before the
  // drop the half-typed mode looked like a complete parameter NAME and the
  // type list replaced the `IN`/`OUT`/`INOUT` the slot actually wants
  // (mysql coverage script.33 §1/§3/§5/§6, 42 §1/§2, 43 §4, 46 §1).
  const tokens = dropAttachedPartial(raw, offset);
  if (!isInsideCreateRoutineParamList(tokens)) return false;
  return paramModePrecededByName(tokens);
}

/**
 * True when the cursor sits inside an unclosed `(` whose enclosing statement
 * head is `CREATE [OR REPLACE] PROCEDURE|FUNCTION` (MySQL spelling — no
 * `OR ALTER`). Mirrors the mssql `isInsideCreateRoutineParamList` shape but
 * keyed to MySQL's `CREATE OR REPLACE` rather than `CREATE OR ALTER`.
 */
function isInsideCreateRoutineParamList(tokens: readonly ClauseToken[]): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx < 0) return false;
  const head = tokens
    .slice(0, lparenIdx)
    .map((t) => upper(t))
    .filter((u): u is string => !!u);
  let i = 0;
  // `findEnclosingStatement` has no DELIMITER-line awareness, so a routine
  // opened with the mysql-client `DELIMITER //` directive carries it in the
  // statement window. Skip `DELIMITER` plus its delimiter token when that
  // token lexes as a word (e.g. `$$`); punctuation delimiters (`//`) are
  // already dropped by the word filter above.
  while (head[i] === 'DELIMITER') {
    i += 1;
    if (head[i] !== undefined && head[i] !== 'CREATE') i += 1;
  }
  if (head[i] !== 'CREATE') return false;
  i += 1;
  if (head[i] === 'OR') {
    i += 1;
    if (head[i] !== 'REPLACE') return false;
    i += 1;
  }
  return head[i] === 'PROCEDURE' || head[i] === 'FUNCTION';
}

/**
 * Confirm the cursor's prev token is an identifier (the param name) whose
 * preamble is a fresh parameter slot: `( <name> ▮` or `( [IN|OUT|INOUT|IN OUT]
 * <name> ▮` (the optional mode keywords sit BEFORE the name in MySQL's `[mode]
 * name type` grammar). The mode keywords are optional (MySQL defaults to `IN`),
 * so the bare `<paramName> ▮` form is also accepted. This is the guard that
 * keeps the emitter from misfiring where `IN` is a predicate keyword rather
 * than a parameter mode.
 */
function paramModePrecededByName(tokens: readonly ClauseToken[]): boolean {
  // The type slot's prev token is the param name itself.
  const name = tokens[tokens.length - 1];
  if (!name || name.kind !== 'identifier') return false;

  // Walk back over up to two optional mode tokens (`INOUT`, or the `IN OUT`
  // pair) that may precede the name, then require a `(` / `,` fresh-slot lead.
  let i = tokens.length - 2;
  let modeSteps = 0;
  while (i >= 0 && modeSteps < 2) {
    const u = upper(tokens[i]);
    if (u !== null && PARAM_MODE_KEYWORDS.has(u)) {
      i -= 1;
      modeSteps += 1;
      continue;
    }
    break;
  }
  const before = tokens[i];
  if (!before) return false;
  return before.kind === 'lparen' || before.kind === 'comma';
}
